import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { ChevronDown, LogOut, Settings, User, Compass, LayoutGrid, Users } from "lucide-react";
import { MoodButton } from "./MoodButton";

const ROLE_LABELS = {
  superadmin: "Superadmin",
  consultant: "Consultant",
  org_admin: "Organisatie-admin",
  viewer: "Viewer",
  member: "Medewerker",
};

export default function Header({ orgs, currentOrgId, currentNodeId, onSelectOrg, myEmployee, onProfileSaved }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const path = useLocation().pathname;
  const qs = [currentOrgId && `org=${currentOrgId}`, currentNodeId && `node=${currentNodeId}`].filter(Boolean).join("&");
  const nav = (to, label, Icon, testid) => (
    <Button variant={path === to ? "default" : "outline"} className={`rounded-none h-9 ${path === to ? "bg-zinc-900" : ""}`} onClick={() => navigate(`${to}${qs ? `?${qs}` : ""}`)} data-testid={testid}>
      <Icon className="h-4 w-4 mr-2" /> {label}
    </Button>
  );
  const [profileOpen, setProfileOpen] = useState(false);
  const [p, setP] = useState(null);

  const canAdmin = ["superadmin", "consultant", "org_admin"].includes(user.role);

  const openProfile = () => {
    setP({ name: myEmployee.name, role: myEmployee.role || "", email: myEmployee.email || "", notes: myEmployee.notes || "" });
    setProfileOpen(true);
  };

  const saveProfile = async () => {
    await api.patch(`/employees/${myEmployee.id}`, p);
    setProfileOpen(false);
    toast.success("Je gegevens zijn bijgewerkt");
    onProfileSaved && onProfileSaved();
  };

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-zinc-900" data-testid="app-header">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 min-h-16">
        <div className="flex items-center gap-5">
          <div className="font-grotesk font-bold tracking-tight text-base whitespace-nowrap">CIRCUMPLEX · VSM</div>
          {orgs.length > 0 && (
            <Select value={currentOrgId || ""} onValueChange={onSelectOrg}>
              <SelectTrigger className="rounded-none border-zinc-900 h-9 w-44 sm:w-56" data-testid="org-selector">
                <SelectValue placeholder="Kies organisatie" />
              </SelectTrigger>
              <SelectContent>
                {orgs.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {user.employee_id && <MoodButton />}
          {myEmployee && (
            <Button variant="outline" className="rounded-none h-9" onClick={openProfile} data-testid="my-profile-button">
              <User className="h-4 w-4 mr-2" /> Mijn gegevens
            </Button>
          )}
          {nav("/", "Model", LayoutGrid, "model-nav-button")}
          {nav("/strategies", "Strategieën", Compass, "strategies-nav-button")}
          {nav("/employees", "Medewerkers", Users, "employees-nav-button")}
          {canAdmin && (
            <Button variant="outline" className="rounded-none h-9" onClick={() => navigate("/admin")} data-testid="admin-nav-button">
              <Settings className="h-4 w-4 mr-2" /> Beheer
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-none h-9" data-testid="user-menu-button">
                {user.name} <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="font-normal">
                <div className="text-sm font-semibold">{user.name}</div>
                <div className="text-xs text-zinc-500">{ROLE_LABELS[user.role]}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => logout().then(() => navigate("/login"))} data-testid="logout-button">
                <LogOut className="h-4 w-4 mr-2" /> Uitloggen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {myEmployee && p && (
        <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
          <DialogContent data-testid="profile-dialog">
            <DialogHeader>
              <DialogTitle className="font-grotesk">Mijn gegevens</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Naam</Label>
                <Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} className="rounded-none border-zinc-900 mt-1" data-testid="profile-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Rol</Label>
                <Input value={p.role} onChange={(e) => setP({ ...p, role: e.target.value })} className="rounded-none border-zinc-900 mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">E-mail</Label>
                <Input value={p.email} onChange={(e) => setP({ ...p, email: e.target.value })} className="rounded-none border-zinc-900 mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Notities</Label>
                <Textarea value={p.notes} onChange={(e) => setP({ ...p, notes: e.target.value })} className="rounded-none border-zinc-900 mt-1" />
              </div>
            </div>
            <DialogFooter>
              <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700" onClick={saveProfile} data-testid="save-profile-button">
                Opslaan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </header>
  );
}
